import { getPublicServiceAppData } from '@/lib/services/public-service.service';
import { QueryFunction } from 'react-query';
import queryClient, { AppDataQueryKey } from '@/lib/query-client';
import { getOrgBasicInfoAppData } from './services/org.service';
import { getAdminUserPermData } from './services/admin-user.service';
import { getAgentsAppData } from './services/agent.service';

const MINUTE = 1000 * 60;
const HOUR = MINUTE * 60;

export type QueryOptions = {
  cacheTime?: number; // how long before garbage collection
  staleTime?: number; // how long before refetch
  enabled?: boolean;
};

export const AppDataQueries: Map<
  AppDataQueryKey,
  { queryFn: QueryFunction; options: QueryOptions; prefetch?: boolean }
> = new Map();

// Public Services Catalog
AppDataQueries.set(AppDataQueryKey.PublicServices, {
  queryFn: getPublicServiceAppData,
  options: {
    staleTime: HOUR * 24,
    cacheTime: HOUR * 48,
  },
});

// Org basic info
AppDataQueries.set(AppDataQueryKey.OrgBasicInfo, {
  queryFn: getOrgBasicInfoAppData,
  options: {
    staleTime: HOUR * 24,
    cacheTime: HOUR * 48,
  },
  prefetch: true,
});

// Admin user permissions
AppDataQueries.set(AppDataQueryKey.AdminUserPermissions, {
  queryFn: getAdminUserPermData,
  options: {
    staleTime: MINUTE * 30,
    cacheTime: MINUTE * 15,
  },
});

// Agents
AppDataQueries.set(AppDataQueryKey.Agents, {
  queryFn: getAgentsAppData,
  options: {
    staleTime: HOUR * 24,
    cacheTime: HOUR * 48,
  },
});

export const fetchAppData = async () => {
  const fetchQueries: Promise<void>[] = [];

  for (const [queryKey, { queryFn, options }] of AppDataQueries) {
    fetchQueries.push(queryClient.fetchQuery(queryKey, queryFn, options));
  }

  return Promise.all(fetchQueries);
};

export const prefetchAppData = async () => {
  const prefetchQueries: Promise<void>[] = [];

  for (const [queryKey, { queryFn, options, prefetch }] of AppDataQueries) {
    if (prefetch) {
      prefetchQueries.push(queryClient.prefetchQuery(queryKey, queryFn, options));
    }
  }

  return Promise.all(prefetchQueries);
};

export const clearAppData = (queryKey: AppDataQueryKey) => {
  queryClient.removeQueries({ queryKey: [queryKey] });
};
